import { fromEvent } from 'rxjs';
import { privacyPolicyParagraph } from '../constants/sharedHtml';

/**
 * LegalText class handles the display and interaction of legal text on lead forms.
 */
export default class LegalText {
  /**
   * Creates an instance of LegalText.
   * @param {string} text - The legal text to display.
   * @param {Array<{key: string, value: string}>} replacements - An array of objects containing keys and values
   * for text replacement.
   */
  constructor(text = '', replacements = []) {
    this.selector = '[data-legal-text-wrapper]';
    this.expanded = false;
    this.showMoreClickSubscription = undefined;
    this.textClassName = '';
    this.replacements = replacements;
    this.text = text;
    this.wrapperId = '';

    /**
     * Handles the click event for the "Show More" button.
     */
    this.clickHandler = () => {
      const wrapper = this.getNode();
      const button = wrapper.querySelector('[data-legal-show-more]');
      const textContainer = wrapper.querySelector('[data-legal-text]');
      const currentlyExpanded = textContainer.dataset.legalTextExpanded === 'true';

      textContainer.innerHTML = this.getText(!currentlyExpanded);
      textContainer.dataset.legalTextExpanded = !currentlyExpanded;

      // this text is the next button label
      button.textContent = !currentlyExpanded ? 'Show Less' : 'Show More';
    };
  }

  /**
   * Sets the legal text to be displayed.
   * @param {string} text
   */
  setText(text) {
    this.text = text;
  }

  /**
   * Overrides the wrapper element's id, and scopes getNode() to look it up by that id instead of
   * the shared `[data-legal-text-wrapper]` selector (which matches the FIRST such element anywhere
   * in the document, including any other LegalText instance or unrelated server-rendered markup
   * using the same attribute). Default (unset) preserves every existing consumer's behavior; a
   * screen that can render alongside another legal-text block on the same page must set this to
   * avoid picking up the wrong node — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} id
   */
  setId(id) {
    this.wrapperId = id;
  }

  /**
   * Overrides the typography class applied to the text span. Default (unset) preserves every
   * existing consumer's `.form__legal` styling (0.7rem/1.2); pass an nhs-o-typography--* class
   * to opt a specific caller into the design-system type scale instead.
   * @param {string} className
   */
  setTextClassName(className) {
    this.textClassName = className;
  }

  /**
   * Id for the inner text span/aria-controls target. Derived from the wrapper id so two
   * instances rendered on the same page (e.g. GetMoreInformationForm alongside another
   * LegalText) don't both emit the fixed 'legal-text-content' id.
   * @returns {string}
   */
  get contentId() {
    return this.wrapperId ? `${this.wrapperId}-content` : 'legal-text-content';
  }

  /**
   * Sets the replacements for the legal text.
   * @param {Array<{key: string, value: string}>} replacements - An array of objects containing keys and values
   *  for text replacement.
   */
  setReplacements(replacements) {
    this.replacements = replacements;
  }

  /**
   * returns the text with the replacements applied it, by default it will return only the first sentence
   * defined as all the text before the first '. ' character sequence
   *
   * @param {boolean} full - whether or not to return the full text instead of the first sentence
   * @returns {string}
   */
  getText(full = false) {
    let legalText = this.text || privacyPolicyParagraph;
    this.replacements.forEach((replacement) => {
      legalText = legalText.replace(replacement.key, replacement.value);
    });
    if (full) {
      return legalText;
    }
    // Find the first period followed by a space to extract the first sentence
    const firstPeriodIndex = legalText.indexOf('. ');
    if (firstPeriodIndex !== -1) {
      return legalText.substring(0, firstPeriodIndex + 1);
    }
    // If no period is found, return the full text as a fallback
    return legalText;
  }

  /**
   * Generates the HTML for the legal text with replacements.
   * @returns {string}
   */
  getHtml() {
    return `
      <div class="form__legal-wrapper" data-legal-text-wrapper${this.wrapperId ? ` id="${this.wrapperId}"` : ''}>
        <div class="form__legal">
         <span id="${this.contentId}" class="${this.textClassName}" data-legal-text data-legal-text-expanded="${this.expanded}">
            ${this.getText(this.expanded)}
          </span>
          <button
           class="nhs-c-standard-link--underlined"
            data-legal-show-more
            aria-expanded="${this.expanded}"
            aria-controls="${this.contentId}"
          >Show More</button>
        </div>
      </div>
    `;
  }

  /**
   * Returns the DOM node for the legal text.
   * @returns {Element}
   */
  getNode() {
    return this.wrapperId ? document.getElementById(this.wrapperId) : document.querySelector(this.selector);
  }

  /**
   * Binds click events to the "Show More" button within the legal text.
   */
  bindEvents() {
    if (!this.getNode()) {
      return;
    }
    const wrapper = this.getNode();
    const button = wrapper.querySelector('[data-legal-show-more]');
    this.showMoreClickSubscription = fromEvent(button, 'click').subscribe(this.clickHandler);
  }

  /**
   * Unbinds the click events from the "Show More" button.
   */
  unbindEvents() {
    if (this.showMoreClickSubscription) {
      this.showMoreClickSubscription.unsubscribe();
      this.showMoreClickSubscription = null;
    }
  }
}
